import { ADD_TO_WISHLIST, REMOVE_FROM_WISHLIST } from "@config/common";
import {
  BRAND_LIST,
  COUNTRY,
  GABrandList,
  GAeCommerceItem,
  GAGlobalOptionType,
  GATrackEventType,
  PageViewBaseOption,
  ProductGAInfoType
} from "@type/index";

export const isClient = typeof window !== "undefined";

let firstPageView = true;
export function checkPreviousPageExists() {
  if (!isClient) {
    return false;
  }

  if (!firstPageView) {
    return true;
  }
  firstPageView = false;

  if (
    window.performance
      .getEntriesByType("navigation")
      .map((nav: any) => nav.type)
      .includes("reload")
  ) {
    return true;
  }

  return !!(
    document.referrer &&
    document.referrer.includes(new URL(window.location.href).hostname)
  );
}

/*
  GU brand must be capitalized for Google Analytics,
  while the internal BRAND_LIST is lowercase "gu"
*/
export const transformBrandForGoogleAnalytics = (
  brand: string
): GABrandList => {
  switch (brand) {
    case BRAND_LIST.UQ:
      return GABrandList.UQ;
    case BRAND_LIST.GU:
      return GABrandList.GU;
    case BRAND_LIST.PL:
      return GABrandList.PL;
    default:
      return GABrandList.UQ;
  }
};

/**
 * sendPageViewForWave2 passes data into window.dataLayer and run callback functions which are injected by native apps if needed.
 * It is called for tracking page view events and virtual page view events.
 *
 * @param data
 */
export const sendPageViewForWave2 = (data: any) => {
  if (!isClient) {
    return;
  }
  if (!window.dataLayer) {
    return;
  }

  if (
    window.UQCustomerAppAndroid &&
    window.UQCustomerAppAndroid.receiveAnalyticsData
  ) {
    // Android App
    window.UQCustomerAppAndroid.receiveAnalyticsData(JSON.stringify(data));
  } else if (
    window.webkit &&
    window.webkit.messageHandlers.receiveAnalyticsData
  ) {
    // iOS App
    window.webkit.messageHandlers.receiveAnalyticsData.postMessage(data);
  }
  // Web Browser and Native App
  window.dataLayer.push(data);
};

export const trackPageView = (baseOption: PageViewBaseOption) => {
  sendPageViewForWave2({
    ...baseOption
  });
};

/**
 * Track an event such as clicking event on GoogleAnalytics
 * via sending data to dataLayer of window object.
 *
 * @param data - Analytics data
 * @param clearEcommerce - Optional boolean to clear previous ecommerce object before pushing event (default: false)
 */
export const sendTrackEvent = (
  data: GATrackEventType | GAGlobalOptionType,
  clearEcommerce = false
): void => {
  if (!isClient) {
    return;
  }
  if (!window.dataLayer) {
    return;
  }

  const eventData = {
    ...data,
    event: data.event || "trackEvent"
  };

  if (
    window.UQCustomerAppAndroid &&
    window.UQCustomerAppAndroid.receiveAnalyticsData
  ) {
    // Android App
    window.UQCustomerAppAndroid.receiveAnalyticsData(JSON.stringify(eventData));
  } else if (
    window.webkit &&
    window.webkit.messageHandlers.receiveAnalyticsData &&
    window.webkit.messageHandlers.receiveAnalyticsData.postMessage
  ) {
    // iOS App
    window.webkit.messageHandlers.receiveAnalyticsData.postMessage(eventData);
  }
  // Web Browser and Native App
  if (clearEcommerce) {
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  }
  window.dataLayer.push(data);
};

export const getBaseGAItemProps = ({
  brand,
  product,
  index
}: {
  brand: BRAND_LIST;
  product: ProductGAInfoType;
  index?: number | null;
}): GAeCommerceItem => ({
  item_id: product?.itemId || "",
  item_product_id: product?.productId || "",
  item_l1_id: "",
  item_name: product?.productName || "",
  index: index != null ? index + 1 : 1,
  item_brand: brand || "",
  item_category: product?.displayGender || "",
  item_category2: "",
  item_category3: "",
  item_category4: "",
  item_list_name: "",
  item_list_type: "",
  item_variant: product?.wishColorName?.toLowerCase() || "",
  item_color_code: product?.wishColorCode || "",
  price: product?.displayPrice ?? 0,
  item_size: product?.wishSize || "",
  item_img_id: product?.imageUrl?.split("/").pop() || "",
  item_search_function: "",
  prc_grp_seq_num: product?.priceGroup || "",
  mk_product_id: getCommunicationCodeForGA(product) || ""
});

export const getGAPropsForAddWishlist = (
  brand: BRAND_LIST,
  product: ProductGAInfoType,
  index: number
) => {
  // del item_search_function
  const { item_search_function, ...baseProps } = getBaseGAItemProps({
    brand,
    product,
    index
  });
  return {
    ...baseProps,
    item_oos: ""
  };
};

export const getGAPropsForRemoveWishlist = (
  brand: BRAND_LIST,
  product: ProductGAInfoType,
  index: number
) => {
  // del item_search_function, item_img_id
  const { item_search_function, item_img_id, ...baseProps } =
    getBaseGAItemProps({
      brand,
      product,
      index
    });
  return {
    ...baseProps,
    item_oos: ""
  };
};

export type GAWishEventType = {
  brand: BRAND_LIST;
  country: COUNTRY;
  productData: ProductGAInfoType;
};

export const trackAddToWishListEvent = (data: GAWishEventType) =>
  sendTrackEvent(
    {
      event: ADD_TO_WISHLIST,
      ecommerce: {
        currency: data.productData?.currency || "",
        value: data.productData?.displayPrice ?? 0,
        items: [
          {
            ...getGAPropsForAddWishlist(
              data.brand,
              data.productData,
              data.productData?.index ?? 0
            )
          }
        ]
      }
    },
    true
  );

export const trackRemoveFromWishListEvent = (data: GAWishEventType) =>
  sendTrackEvent(
    {
      event: REMOVE_FROM_WISHLIST,
      ecommerce: {
        currency: data.productData?.currency || "",
        value: data.productData?.displayPrice ?? 0,
        custom_item_id: data.productData?.itemId || "",
        custom_item_product_id: data.productData?.productId || "",
        custom_item_l1_id: "",
        items: [
          {
            ...getGAPropsForRemoveWishlist(
              data.brand,
              data.productData,
              data.productData?.index ?? 0
            )
          }
        ]
      }
    },
    true
  );

export const getCommunicationCodeForGA = (
  product: ProductGAInfoType
): string => {
  const productCode = product?.productId?.split("-")[0] ?? "";
  const colorCode = product?.wishColorCode ?? "";
  const sizeCode = product?.wishSizeCode ?? "";
  const lengthCode = product?.pld?.displayCode ?? "000";
  if (productCode && colorCode && sizeCode && lengthCode) {
    return `${productCode}-${colorCode}-${sizeCode}-${lengthCode}`;
  }
  return "";
};
